import {
  clearManageFlowBooking,
  deleteRefundOffer,
  getManageFlowBookingById,
  getManageFlowTravelAccountsMap,
  initiateLegsRefund,
  initiateNonTripOffersRefund,
  resetChangeTripsSelection,
  resetRefundSelection,
  setChangeTripsSelectedTripId,
  toggleRefundLeg,
  toggleRefundLegAdmission,
  toggleRefundNonTripAdmission,
} from '@/features/manage/manageActions';
import type {
  AdmissionDTO,
  BookingDTO,
  RefundOffersDTO,
} from '@/types/dto/agent';
import type { TravelAccountsMap } from '@/types/travelpass';
import { orderBookingTripsBasedOnDepartureTime } from '@/utils/booking';
import { createReducer } from '@reduxjs/toolkit';

type ManageBookingState = {
  refund: {
    selectedLegAdmissions: { [legId: string]: Array<AdmissionDTO> };
    selectedNonTripAdmissions: { [admissionId: string]: AdmissionDTO };
    details: RefundOffersDTO | null;
  };
  changeTrips: {
    outboundTripId: string | null;
    returnTripId: string | null;
  };
  booking: BookingDTO | null;
  travelAccountsMap?: TravelAccountsMap;
};

const initialState: ManageBookingState = {
  refund: {
    selectedLegAdmissions: {},
    selectedNonTripAdmissions: {},
    details: null,
  },
  changeTrips: {
    outboundTripId: null,
    returnTripId: null,
  },
  booking: null,
};

export const manageReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(toggleRefundLeg, (state, action) => {
      const { legId, admissions } = action.payload;
      if (admissions.length > 0) {
        state.refund.selectedLegAdmissions[legId] = admissions;
      } else {
        state.refund.selectedLegAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedLegAdmissions).filter(
            ([key]) => key !== legId
          )
        );
      }
    })
    .addCase(toggleRefundLegAdmission, (state, action) => {
      const { legId, admissions } = action.payload;
      if (admissions.length > 0) {
        state.refund.selectedLegAdmissions[legId] = admissions;
      } else {
        state.refund.selectedLegAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedLegAdmissions).filter(
            ([key]) => key !== legId
          )
        );
      }
    })
    .addCase(toggleRefundNonTripAdmission, (state, action) => {
      const admissionId = action.payload.id;

      if (!admissionId) {
        return;
      }

      if (state.refund.selectedNonTripAdmissions[admissionId]) {
        state.refund.selectedNonTripAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedNonTripAdmissions).filter(
            ([key]) => key !== admissionId
          )
        );
      } else {
        state.refund.selectedNonTripAdmissions[admissionId] = action.payload;
      }
    })
    .addCase(resetRefundSelection, (state) => {
      state.refund.selectedLegAdmissions = {};
      state.refund.selectedNonTripAdmissions = {};
      state.refund.details = null;
    })
    .addCase(initiateLegsRefund.fulfilled, (state, action) => {
      state.refund.details = action.payload;
    })
    .addCase(initiateNonTripOffersRefund.fulfilled, (state, action) => {
      state.refund.details = action.payload;
    })
    .addCase(deleteRefundOffer.fulfilled, (state) => {
      state.refund.details = null;
    })
    .addCase(setChangeTripsSelectedTripId, (state, action) => {
      const { tripId, isOutbound } = action.payload;
      if (isOutbound) {
        state.changeTrips.outboundTripId = tripId;
      } else {
        state.changeTrips.returnTripId = tripId;
      }
    })
    .addCase(resetChangeTripsSelection, (state) => {
      state.changeTrips.outboundTripId = null;
      state.changeTrips.returnTripId = null;
    })
    .addCase(getManageFlowBookingById.fulfilled, (state, action) => {
      state.booking = orderBookingTripsBasedOnDepartureTime(action.payload);
    })
    .addCase(clearManageFlowBooking, (state) => {
      Object.assign(state, {
        ...initialState,
      });
    })
    .addCase(getManageFlowTravelAccountsMap.fulfilled, (state, action) => {
      state.travelAccountsMap = action.payload;
    });
});
